/**
 * Module summary:
 * This layout component wraps the authenticated app chrome. It manages the
 * sidebar, top bar, route-aware theming, responsive navigation behavior, and
 * the shared frame around page content.
 */

import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import './AppShell.css';

interface AppShellProps {
  title: string;
  children: React.ReactNode;
  isDashboard?: boolean; // <-- 1. Added this new prop
}

const navItems = [
  // Define the main authenticated destinations in one place so the sidebar and routing stay aligned.
  { to: '/home', label: 'Dashboard' },
  { to: '/create-assessment', label: 'New Assessment' },
  { to: '/reports', label: 'Reports' },
  { to: '/profile', label: 'My Profile' },
  { to: '/about', label: 'About Us' },
  { to: '/contact', label: 'Contact Us' }
];

export const AppShell: React.FC<AppShellProps> = ({ title, children, isDashboard }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const [viewport, setViewport] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
  const mainRef = useRef<HTMLElement>(null);
  const navRef = useRef<HTMLElement>(null);

  const isDesktop = viewport === 'desktop';
  const isMobile = viewport === 'mobile';
  const darkTopbarRoutes = ['/home', '/reports', '/about', '/contact', '/profile'];
  const hasDarkTopTone = darkTopbarRoutes.some((route) => location.pathname.startsWith(route));
  const isCreateAssessmentRoute = location.pathname.startsWith('/create-assessment');

  useEffect(() => {
    const mobileQuery = window.matchMedia('(max-width: 767px)');
    const tabletQuery = window.matchMedia('(min-width: 768px) and (max-width: 1023px)');

    const applyViewport = () => {
      if (mobileQuery.matches) {
        // Collapse the navigation on small screens so the content gets the full viewport width.
        setViewport('mobile');
        setNavOpen(false);
        return;
      }
      if (tabletQuery.matches) {
        // Keep the navigation open on tablet widths because there is enough room for both panels.
        setViewport('tablet');
        setNavOpen(true);
        return;
      }
      setViewport('desktop');
      setNavOpen(true);
    };

    applyViewport();
    mobileQuery.addEventListener('change', applyViewport);
    tabletQuery.addEventListener('change', applyViewport);

    return () => {
      mobileQuery.removeEventListener('change', applyViewport);
      tabletQuery.removeEventListener('change', applyViewport);
    };
  }, []);

  useEffect(() => {
    if (!isDesktop) {
      // Close the mobile/tablet nav after route changes so the next page is immediately visible.
      setNavOpen(false);
      requestAnimationFrame(() => {
        mainRef.current?.focus();
      });
    }
  }, [location.pathname, isDesktop]);

  useEffect(() => {
    if (isDesktop || !navOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Let the Escape key dismiss the mobile nav so keyboard users can exit the overlay quickly.
        setNavOpen(false);
        requestAnimationFrame(() => {
          mainRef.current?.focus();
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDesktop, navOpen]);

  useEffect(() => {
    if (!isMobile) {
      document.body.style.overflow = '';
      return;
    }

    // Lock body scrolling while the mobile menu is open so the overlay behaves like a modal drawer.
    document.body.style.overflow = navOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, navOpen]);

  useEffect(() => {
    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (!metaTheme) return;
    // Update the browser theme color to match the current route tone for a more cohesive mobile chrome.
    metaTheme.setAttribute('content', hasDarkTopTone ? '#173217' : '#eef3ee');
    document.body.style.backgroundColor = hasDarkTopTone ? '#173217' : '#eef3ee';
    document.documentElement.style.backgroundColor = hasDarkTopTone ? '#173217' : '#eef3ee';
  }, [hasDarkTopTone]);

  const onLogout = async () => {
    // End the current session first, then return the user to the public login route.
    await logout();
    navigate('/login');
  };

  const toggleNavigation = () => {
    // Flip the drawer state so one button can both open and close the responsive nav.
    setNavOpen(prev => !prev);
  };

  return (
    <div
      className={[
        'shell',
        `shell--${viewport}`,
        navOpen ? 'shell--open' : 'shell--closed',
      ].join(' ')}
    >
      {isMobile && navOpen && (
        <button
          type="button"
          className="shell__overlay"
          onClick={() => setNavOpen(false)}
          aria-label="Close navigation menu"
        />
      )}
      <aside
        ref={navRef}
        className="shell__sidebar"
        aria-hidden={!isDesktop && !navOpen}
      >
        <div className="brand">
          <img src="/Transparent-Logo.png" alt="Macha Group" className="brand__logo" />
          <div>
            <h1 className="brand__title">Macha Group</h1>
            <p className="brand__subtitle">Security Platform</p>
          </div>
        </div>

        <nav id="main-shell-nav" className="shell__nav">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `shell__link ${isActive ? 'shell__link--active' : ''}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="shell__user">
          <p className="shell__user-name">{user?.displayName ?? 'Guest'}</p>
          <p className="shell__user-email">{user?.email}</p>
          <button className="shell__logout" onClick={onLogout}>Sign out</button>
        </div>
      </aside>

      {/* 2. Added dynamic classes to remove padding when on the Dashboard */}
      <main
        ref={mainRef}
        className={`shell__main ${isDashboard ? 'shell__main--dashboard' : ''} ${hasDarkTopTone ? 'shell__main--tone-dark' : 'shell__main--tone-light'} ${isCreateAssessmentRoute ? 'shell__main--route-create' : ''}`}
        tabIndex={-1}
      >
        {!isDesktop && (
          <div className={`shell__topbar ${isDashboard ? 'shell__topbar--overlay' : ''}`}>
            <button
              type="button"
              className="shell__menu-button"
              onClick={toggleNavigation}
              aria-label={navOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={navOpen}
              aria-controls="main-shell-nav"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div className="shell__topbar-copy">
              <p className="shell__topbar-label">Navigation</p>
              <h2>{title}</h2>
            </div>
          </div>
        )}
        {!isDashboard && isDesktop && (
          <header className="shell__header">
            <h2>{title}</h2>
          </header>
        )}
        <section className={`shell__content ${isDashboard ? 'shell__content--dashboard' : ''}`}>
          {children}
        </section>
      </main>
    </div>
  );
};

export default AppShell;
